import React, { useState, useEffect } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import axios from "axios";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import { format, toZonedTime } from "date-fns-tz";

const StepSummary = ({ selectedRooms, guestDetails, finalcheckInDate, finalcheckOutDate, paymentSummary, handleBack }) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const [localCheckInDate, setLocalCheckInDate] = useState(() => {
    return finalcheckInDate ? new Date(finalcheckInDate) : new Date();
  });
  const [localCheckOutDate, setLocalCheckOutDate] = useState(() => {
    return finalcheckOutDate ? new Date(finalcheckOutDate) : new Date();
  });

  useEffect(() => {
    if (finalcheckInDate) {
      setLocalCheckInDate(new Date(finalcheckInDate));
      console.log("Parsed checkInDate:", new Date(finalcheckInDate));
    }
    if (finalcheckOutDate) {
      setLocalCheckOutDate(new Date(finalcheckOutDate));
      console.log("Parsed checkOutDate:", new Date(finalcheckOutDate));
    }
  }, [finalcheckInDate, finalcheckOutDate]);

  const [advancePayment, setAdvancePayment] = useState();
  const [paymentMode, setPaymentMode] = useState(null);
  const [paymentReference, setPaymentReference] = useState("");
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const [roomAllocations, setRoomAllocations] = useState({});
  const navigate = useNavigate();
  const currency = localStorage.getItem("currencycode");
  const storedSearchParams = localStorage.getItem("searchParams");
  const parsedParams = storedSearchParams ? JSON.parse(storedSearchParams) : {};
  const business_date = localStorage.getItem("business_date");

  // Log parsedParams for debugging
  useEffect(() => {
    console.log("Initial parsedParams:", parsedParams);
    console.log("Initial bookingSource:", parsedParams.bookingSource);
  }, []);

  const paymentModes = [
    { value: "CASH", label: "Cash" },
    { value: "BANK_TRANSFER", label: "Bank Transfer" },
    { value: "UPI", label: "UPI" },
    { value: "CARD", label: "Credit Card" },
    { value: "CHEQUE", label: "Cheque" },
  ];

  // Function to Handle Confirmation
  const handleConfirmBooking = async () => {
    if (advancePayment > 0) {
      if (!paymentMode) {
        Swal.fire("Validation Error", "Please select a Mode of Payment.", "error");
        return;
      }
    }

    Swal.fire({
      title: "Confirm Booking?",
      text: "Are you sure you want to proceed with this booking?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Confirm",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);

          // Log request data for debugging
          console.log("parsedParams before API call:", parsedParams);
          console.log("bookingSource before API call:", parsedParams.bookingSource);

          const requestData = {
            guestDetails,
            selectedRooms,
            finalcheckInDate,
            finalcheckOutDate,
            paymentSummary,
            advancePayment,
            paymentMode,
            paymentReference,
            nights: parsedParams.nights || 1,
            bookingSource: parsedParams.bookingSource?.value || "",
            corporateCompany: parsedParams.corporateCompany || null,
            rateCard: parsedParams.rateCard || "",
            marketCode: parsedParams.marketCode || "",
            reservationType: parsedParams.reservationType || "",
          };

          console.log("Request data sent to API:", requestData);

          const response = await axios.post(
            `${apiUrl}/booking/add-booking`,
            requestData,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          console.log("API response:", response.data);

          if (response.status === 200 || response.status === 201) {
            const reservationId = response.data.bookingId;

            // Clear localStorage after successful booking
            localStorage.removeItem("selectedRooms");
            localStorage.removeItem("billingSummary");
            localStorage.removeItem("searchState");
            localStorage.removeItem("searchParams");
            localStorage.removeItem("filteredRooms");

            const secretKey = "abde100199199111";
            const encryptedId = CryptoJS.AES.encrypt(reservationId.toString(), secretKey).toString();

            Swal.fire({
              title: "Booking Confirmed!",
              text: "Your booking has been successfully saved.",
              icon: "success",
              confirmButtonText: "OK",
            }).then(() => {
              navigate(`/hospitality/reservation-details/${encodeURIComponent(encryptedId)}`);
            });
          } else {
            throw new Error("Booking failed with status: " + response.status);
          }
        } catch (error) {
          console.error("Booking error:", error.response?.data || error.message);
          Swal.fire("Error", "Failed to confirm booking. Please try again. "+ error.message, "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not Available";
    const utcDate = new Date(dateStr);
    const zonedDate = toZonedTime(utcDate, userTimezone);
    return format(zonedDate, "MMMM d, yyyy", { timeZone: userTimezone });
  };

  return (
    <div className="step-summary">
      {/* Reservation Summary Section */}
      <div className="row">
        <div className="col-md-8">
          <div className="card mb-4 form-bg">
            <div className="card-header bg-primary text-white">
              Reservation Summary
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <p><strong>Check-In:</strong> {formatDate(finalcheckInDate)}</p>
                  <p><strong>Check-Out:</strong> {formatDate(finalcheckOutDate)}</p>
                  <p><strong>Nights:</strong> {parsedParams.nights || 1}</p>
                  <p><strong>Reservation Date:</strong> {formatDate(business_date)}</p>
                  <p><strong>Source:</strong> {parsedParams.bookingSource?.name || "Not Specified"}</p>
                </div>
                <div className="col-md-6">
                  <p><strong>Guest:</strong> {guestDetails?.name || "Not Provided"}</p>
                  <p><strong>Email:</strong> {guestDetails?.email || "Not Provided"}</p>
                  <p><strong>Phone:</strong> {guestDetails?.phone || "Not Provided"}</p>
                  <p><strong>Country:</strong> {guestDetails?.country || "Not Provided"}</p>
                  <p><strong>Estimated Arrival Time:</strong> {guestDetails?.expected_time || "Not Provided"}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Accommodations Summary */}
          <div className="card form-bg">
            <div className="card-header bg-primary text-white">
              Accommodations Summary
            </div>
            <div className="card-body">
<table className="table table-bordered">
  <thead className="table-light">
    <tr>
      <th>Type</th>
      <th>Room No.</th>
      <th>Arrival / Departure</th>
      <th>Guests</th>
      <th>Meal Plan</th>
      <th>Room Charge</th> {/* New column for Room Charge */}
      <th>Tax</th> {/* New column for Tax */}
      <th>Total</th>
    </tr>
  </thead>
  <tbody>
    {selectedRooms.length > 0 ? (
      selectedRooms.flatMap((room) =>
        [...Array(room.quantity)].map((_, roomIndex) => {
          let roomRate = room.appliedRate?.selected ?? room.originalRate?.single ?? parseFloat(room.rate_single);
          if (room.isComplimentary) {
            roomRate = 0;
          }

          const nights = Math.max(
            1,
            Math.ceil((new Date(finalcheckOutDate) - new Date(finalcheckInDate)) / (1000 * 60 * 60 * 24))
          );
          const roomCharges = roomRate * nights; // Per-room charge

          // Adjust tax details by dividing taxAmount by quantity to get per-room tax
          const adjustedTaxDetails = room.taxDetails
            ? room.taxDetails.map(tax => ({
                taxName: tax.taxName,
                taxRate: tax.taxRate,
                taxAmount: tax.taxAmount / room.quantity, // Divide by quantity to get per-room tax
              }))
            : [];

          const roomTaxTotal = adjustedTaxDetails.reduce((sum, tax) => sum + (tax.taxAmount || 0), 0);
          const roomTotal = room.isComplimentary || room.excludeTax
            ? roomCharges
            : roomCharges + roomTaxTotal;

          return (
            <tr key={`${room.id}-${roomIndex}`}>
              <td>{room.type}</td>
              <td>{room.roomNumbers?.[roomIndex] || "Not Assigned"}</td>
              <td>
                {new Date(finalcheckInDate).toLocaleDateString()} - {new Date(finalcheckOutDate).toLocaleDateString()}
              </td>
              <td>
                {room.adults} Adults, {room.children} Children
              </td>
              <td>{room.mealPlan}</td>
              <td>{currency} {roomCharges.toFixed(2)}</td> {/* New Room Charge column */}
              <td>{currency} {roomTaxTotal.toFixed(2)}</td> {/* New Tax column */}
              <td>{currency} {roomTotal.toFixed(2)}</td>
            </tr>
          );
        })
      )
    ) : (
      <tr>
        <td colSpan="8" className="text-center">No rooms selected.</td> {/* Updated colSpan to 8 */}
      </tr>
    )}
  </tbody>
</table>
            </div>
          </div>
        </div>

        {/* Payment Information */}
        <div className="col-md-4">
          <div className="card p-3 border rounded bg-white">
            <div className="card-header bg-primary text-white">
              Payment Information
            </div>
            <div className="card-body">
              {paymentSummary ? (
                <>
                  <div className="d-flex justify-content-between border-bottom pb-2">
                    <span className="fw-bold text-secondary">Room Charges</span>
                    <span className="fw-bold">
                      {currency} {paymentSummary.roomCharges?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  {paymentSummary.taxDetails && paymentSummary.taxDetails.map((tax, index) => (
                    <div
                      key={index}
                      className="d-flex justify-content-between border-bottom pb-2"
                    >
                      <span className="fw-bold text-secondary">
                        {tax.taxName} ({tax.taxRate}%)
                      </span>
                      <span className="fw-bold">
                        {currency} {tax.taxAmount.toFixed(2)}
                      </span>
                    </div>
                  ))}
                  <div className="d-flex justify-content-between border-bottom pb-2">
                    <span className="fw-bold text-dark">Grand Total</span>
                    <span className="fw-bold text-primary">
                      {currency} {paymentSummary.totalAmount?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between pt-2">
                    <span className="fw-bold text-dark">Balance Due</span>
                    <span className="fw-bold text-primary">
                      {currency} {(paymentSummary.totalAmount - (advancePayment || 0)).toFixed(2)}
                    </span>
                  </div>
                </>
              ) : (
                <p className="text-danger">No payment summary available. Please go back and select a room.</p>
              )}
              <div className="form-group mt-3">
                <label><strong>Advance Payment ({currency})</strong></label>
                <input
                  type="number"
                  className="form-control"
                  value={advancePayment}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setAdvancePayment("");
                    } else {
                      const numValue = Number(value);
                      if (numValue >= 0 && numValue <= (paymentSummary.totalAmount || 0)) {
                        setAdvancePayment(numValue);
                      }
                    }
                  }}
                  min="0"
                  max={paymentSummary.totalAmount || 0}
                  placeholder="Enter advance amount"
                />
              </div>
              <div className="form-group mt-3">
                <label><strong>Mode of Advance Payment</strong></label>
                <Select
                  options={paymentModes}
                  value={paymentModes.find(mode => mode.value === paymentMode)}
                  onChange={(selectedOption) => setPaymentMode(selectedOption ? selectedOption.value : null)}
                  placeholder="Select Payment Mode"
                  isClearable
                />
              </div>
              <div className="form-group mt-3">
                <label><strong>Payment Reference</strong></label>
                <input
                  type="text"
                  className="form-control"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Enter transaction reference (if any)"
                />
              </div>
              <div className="button-group mt-4 d-flex justify-content-end" style={{ gap: "1rem" }}>
                <button className="btn btn-secondary mr-2 custom-large-button" onClick={handleBack}>
                  Back
                </button>
                <button
                  className="btn btn-success ml-2 custom-large-button"
                  onClick={handleConfirmBooking}
                  disabled={loading}
                >
                  {loading ? <Spin size={15} color="#fff" /> : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepSummary;